// ============================================================================
// FILE:    src/app/core/auth/roles.enum.ts
// VERSION: v2.0.0 — RBAC Phase 2: role_key-basierte Projektrollen
// DATE:    2026-05-09
//
// ZWECK:
//   Zentrale Enum-Definition aller Rollen im System.
//   Wird von role-capabilities.map.ts, permission-role.map.ts und
//   CapabilityService verwendet.
//
// ZWEI ACHSEN:
//   A) Org-Rollen  → ciamreg.permission  → wer darf die App verwalten?
//   B) Projekt-Rollen → access.role_key  → wer darf was im Projekt tun?
//
// ORG-ROLLEN (A):
//   NONE          → orgPerm 0: kein Zugang
//   APP_USER      → orgPerm 1: normaler App-User, benötigt CAL
//   PMO_OFFICER   → orgPerm 3: App-Management (Bulk-Provisioning, CAL-Vergabe,
//                              Thirdparty-Config, User-Verwaltung). Eine Person,
//                              die ALLES Organisatorische für das Team regelt.
//                              Früher aufgeteilt auf Werte 3, 5, 6 — zusammengelegt
//                              weil eine Person im Konzern diese Aufgaben trägt,
//                              nicht ein Team. Einzel-Schwellwert spart Login-Overhead.
//   SUPER_ADMIN   → orgPerm 7: Hardcoded Fallback-User. Nur für Notfälle und
//                              initiales Setup. Bypassed alle Guards (CAL + Capability).
//
// PROJEKT-ROLLEN (B):
//   PROJECT_IDLE    → perm 0, access_state != 'approved': kein Projekt-Zugang.
//                     Zustand nach Request oder nach Reject/Revoke.
//                     Sieht nur /request-access Page.
//
//   PROJECT_VIEWER  → perm 1, role_key 'project_viewer': nur lesen.
//                     Für externe Stakeholder, Kunden, Auditoren.
//                     KEIN Daily-Edit, KEIN Task-Create. Begründung: ein externer
//                     Stakeholder soll NIE in den täglichen Team-Rhythmus schreiben.
//
//   PROJECT_MEMBER  → perm 1, role_key 'project_member': ausführendes Teammitglied.
//                     Developer, Tester, BA/RE in der Ausführungsrolle.
//                     Erstellt eigene Tasks, füllt Daily aus.
//                     KEIN Sprint-Create, KEIN Item-Create — das ist Planungsarbeit.
//
//   PROJECT_IT_PM   → perm 3, role_key 'project_it_pm': Planungsrolle.
//                     PO, SM, BA/RE in der Planungsrolle, Product Manager.
//                     Zusammengelegt weil alle dieselben Schreibrechte auf
//                     Planungsartefakte (Sprint, Item, Milestone, Persona, Solution)
//                     benötigen. Kein Feature in ceeq.it ist sinnvoll für PO aber
//                     nicht für SM — oder umgekehrt.
//
//   PROJECT_ADMIN   → perm 5, role_key 'project_admin': Projekt-Administration.
//                     Alles von IT-PM + Manage Access (wer darf ins Projekt).
//                     Typisch: IT-Projektleiter, Lead-Developer mit Admin-Aufgaben.
//
// HINWEIS SAFe-ROLLEN (RTE, Solution Architect, etc.):
//   Werden in Phase 2 als eigene role_keys ergänzt sobald Features gebaut werden
//   die exklusiv für diese Rollen relevant sind. Aktuell als PROJECT_IT_PM oder
//   PROJECT_ADMIN abgedeckt. Keine Restriktionen vorbauen die später blockieren.
//
// HINWEIS LEGACY:
//   Der Wert 'MANAGER' und 'ADMIN' aus v1.x sind ersetzt durch PROJECT_IT_PM /
//   PROJECT_ADMIN. permission-role.map.ts brückt den Übergang.
// ============================================================================
// ============================================================================
// FILE:    src/app/core/auth/roles.enum.ts
// VERSION: v2.1.0 — Bugfix: ROLE_LABELS Export hinzugefügt
// DATE:    2026-05-10
//
// ÄNDERUNG v2.1.0:
//   ROLE_LABELS Map ergänzt — wird von InsufficientPermissionPageComponent
//   importiert um die aktuelle Rolle des Users lesbar anzuzeigen.
//   Import-Fehler: "has no exported member 'ROLE_LABELS'" behoben.
//
// Kein anderer Inhalt geändert gegenüber v2.0.0.
// ============================================================================

export enum Role {

  // ── Org-Rollen (ciamreg.permission) ──────────────────────────────────────

  /** orgPerm 0: Kein Zugang zur App */
  NONE        = 'none',

  /** orgPerm 1: Normaler App-User. Benötigt CAL. Kann Projekte nutzen. */
  APP_USER    = 'app_user',

  /**
   * orgPerm 3: PMO Officer. App-Management komplett:
   * User anlegen, Permissions setzen, Projekte erstellen,
   * Thirdparty-Config, Bulk Provisioning, CALs zuweisen/revoken.
   * Benötigt CAL (außer für App-Management-Login-Page).
   */
  PMO_OFFICER = 'pmo_officer',

  /**
   * orgPerm 7: Super Admin. Hardcoded Fallback.
   * Bypassed ALLE Guards. Kein CAL-Check. Für Notfälle.
   */
  SUPER_ADMIN = 'super_admin',

  // ── Projekt-Rollen (access.role_key) ─────────────────────────────────────

  /**
   * perm 0, access_state != 'approved':
   * Kein aktiver Projekt-Zugang. Sieht nur /request-access.
   * Umbenennung von _PENDING zu _IDLE: access_state enthält die Details
   * (pending / rejected / revoked — neutralere Bezeichnung).
   */
  PROJECT_IDLE   = 'project_idle',

  /**
   * perm 1, role_key 'project_viewer':
   * Nur lesen. Externe Stakeholder, Kunden, Auditoren.
   * Bewusst KEIN Daily-Edit und KEIN Task-Create:
   * Externe sollen nie in den Team-Arbeitsrhythmus schreiben.
   */
  PROJECT_VIEWER = 'project_viewer',

  /**
   * perm 1, role_key 'project_member':
   * Ausführendes Teammitglied. Developer, Tester, BA/RE Ausführung.
   * Erstellt eigene Tasks, füllt Daily-Scrum-Matrix aus.
   * Kein Sprint-Create / Item-Create — das ist Planungsarbeit (IT-PM).
   */
  PROJECT_MEMBER = 'project_member',

  /**
   * perm 3, role_key 'project_it_pm':
   * LEGACY-KOMPOSIT. Planungsrolle PO/SM/BA-RE/Product Manager.
   * Ab dem orthogonalen Modell (2026-06) = PLANNER ∪ SOLUTION_AUTHOR.
   * Bleibt erhalten für Bestands-Access-Einträge mit diesem role_key und als
   * "beides"-Sammelrolle. Neue Vergaben nutzen die schmaleren Scopes unten.
   */
  PROJECT_IT_PM  = 'project_it_pm',

  /**
   * perm 3, role_key 'project_planner':
   * SCOPE "Team-Planung" — Items, Sprints, Milestones erstellen/bearbeiten.
   * PO/SM/Team-Planer. Orthogonal zu SOLUTION_AUTHOR (anderer Scope, nicht
   * höhere Macht). Enthält die Member-Basics (View/Daily/Tasks).
   */
  PROJECT_PLANNER = 'project_planner',

  /**
   * perm 3, role_key 'project_solution_author':
   * SCOPE "SAFe-Elemente" — Solution-Canvas, Personas (größere SAFe-Artefakte).
   * Solution Manager/Epic Owner/RTE-nah. Orthogonal zu PLANNER: braucht NICHT
   * zwingend Item/Sprint-Rechte. Enthält die Member-Basics.
   */
  PROJECT_SOLUTION_AUTHOR = 'project_solution_author',

  /**
   * perm 5, role_key 'project_admin':
   * Projekt-Administration. Alles von IT-PM + Manage Access.
   * Gatekeeper: wer darf in welcher Rolle ins Projekt.
   */
  PROJECT_ADMIN  = 'project_admin',
}

// ============================================================================
// ROLE_LABELS — lesbare Bezeichnungen für jede Rolle
//
// VERWENDUNG:
//   import { ROLE_LABELS } from '../../core/auth/roles.enum';
//   this.yourRoleLabel = ROLE_LABELS[role] || role;
//
// ZEIGT in insufficient-permission-page:
//   "Your current role: IT Project Manager"
//   → erklärt dem User warum er keinen Zugang hat und was er hat
// ============================================================================
export const ROLE_LABELS: Record<Role, string> = {
  [Role.NONE]:           'No Access',
  [Role.APP_USER]:       'App User',
  [Role.PMO_OFFICER]:    'PMO Officer',
  [Role.SUPER_ADMIN]:    'Super Admin',
  [Role.PROJECT_IDLE]:             'Idle (No Project Access)',
  [Role.PROJECT_VIEWER]:           'Viewer',
  [Role.PROJECT_MEMBER]:           'Team Member',
  [Role.PROJECT_IT_PM]:            'IT-PM (Planner + Solution)',
  [Role.PROJECT_PLANNER]:          'Planner (Items/Sprints/Milestones)',
  [Role.PROJECT_SOLUTION_AUTHOR]:  'Solution Author (SAFe)',
  [Role.PROJECT_ADMIN]:            'Project Admin',
};
